import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"
import { pageLocationHasPii } from "../utils/pageLocation"
import { formatDate, formatNumber } from "../utils/formatters"

export default function GA4PageLocationAudit() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const [auditResults, setAuditResults] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(null)
  const [ignoreRules, setIgnoreRules] = useState([])
  const [rulesLoading, setRulesLoading] = useState(false)
  const [rulesError, setRulesError] = useState(null)
  const [newUtmParameter, setNewUtmParameter] = useState("")
  const [newUtmValue, setNewUtmValue] = useState("")
  const [addingUtmRule, setAddingUtmRule] = useState(false)
  const [resolvingUrl, setResolvingUrl] = useState(null)
  const [removingRuleId, setRemovingRuleId] = useState(null)
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setAuditResults([])
      setAuditError(propertyError || null)
      setAuditLoading(false)
      return
    }

    let cancelled = false

    const loadAudit = async () => {
      setAuditLoading(true)
      setAuditError(null)
      try {
        const { data: audit, error } = await supabase
          .from("ga4_page_location_audit_results")
          .select(
            "page_location, pii_matches, event_count, first_detected, last_detected"
          )
          .eq("ga4_property_id", propInfo.id)
          .order("event_count", { ascending: false, nullsFirst: false })
          .order("page_location", { ascending: true })
        if (error) throw error
        if (cancelled) return
        setAuditResults(
          (audit || [])
            .map((row) => ({
              page_location:
                typeof row?.page_location === "string" ? row.page_location : "",
              first_detected: row?.first_detected || null,
              last_detected: row?.last_detected || null,
              event_count:
                typeof row?.event_count === "number" && Number.isFinite(row.event_count)
                  ? row.event_count
                  : null,
              pii_matches: Array.isArray(row?.pii_matches)
                ? row.pii_matches
                    .map((match) => normalizeMatch(match))
                    .filter(Boolean)
                : [],
            }))
            .filter((row) => pageLocationHasPii(row.page_location, row.pii_matches))
        )
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 page location audit", err)
        setAuditResults([])
        setAuditError(err)
      } finally {
        if (!cancelled) setAuditLoading(false)
      }
    }

    loadAudit()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  const loading = propertyLoading || auditLoading || rulesLoading

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setIgnoreRules([])
      setRulesError(propertyError || null)
      setRulesLoading(false)
      return
    }

    let cancelled = false

    const loadRules = async () => {
      setRulesLoading(true)
      setRulesError(null)
      try {
        const { data, error } = await supabase
          .from("ga4_page_location_ignore_rules")
          .select("id, rule_type, parameter, value, created_at")
          .eq("ga4_property_id", propInfo.id)
          .order("created_at", { ascending: true })
        if (error) throw error
        if (cancelled) return
        setIgnoreRules(Array.isArray(data) ? data : [])
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 page location ignore rules", err)
        setIgnoreRules([])
        setRulesError(err)
      } finally {
        if (!cancelled) setRulesLoading(false)
      }
    }

    loadRules()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  const normalizedIgnoreData = useMemo(() => {
    const resolved = new Set()
    const utmRules = []
    for (const rule of ignoreRules) {
      if (!rule || typeof rule !== "object") continue
      const type = typeof rule.rule_type === "string" ? rule.rule_type : ""
      if (!type) continue
      const value = typeof rule.value === "string" ? rule.value : ""
      if (!value) continue
      if (type === "page_location") {
        const normalizedValue = value.trim() || value
        resolved.add(normalizedValue)
        resolved.add(value)
        continue
      }
      if (type === "utm_parameter") {
        const parameter =
          typeof rule.parameter === "string" && rule.parameter.trim()
            ? rule.parameter.trim()
            : ""
        if (!parameter) continue
        utmRules.push({
          id: rule.id || null,
          parameter: parameter.toLowerCase(),
          value: value.toLowerCase(),
        })
      }
    }
    return { resolved, utmRules }
  }, [ignoreRules])

  const groupedResults = useMemo(() => {
    if (!auditResults.length) return []

    const shouldIgnoreLocation = (pageLocation) => {
      if (!pageLocation) return false
      const trimmedLocation = pageLocation.trim()
      if (
        normalizedIgnoreData.resolved.has(pageLocation) ||
        (trimmedLocation && normalizedIgnoreData.resolved.has(trimmedLocation))
      ) {
        return true
      }

      let parsed = null
      try {
        parsed = new URL(pageLocation)
      } catch (error) {
        try {
          const normalized = pageLocation.startsWith("http")
            ? pageLocation
            : `https://${pageLocation.replace(/^\/+/, "")}`
          parsed = new URL(normalized)
        } catch (err) {
          parsed = null
        }
      }

      if (!parsed) return false

      for (const rule of normalizedIgnoreData.utmRules) {
        if (!rule.parameter || !rule.value) continue
        const matchValue = parsed.searchParams.get(rule.parameter)
        if (!matchValue) continue
        if (matchValue.toLowerCase().includes(rule.value)) return true
      }

      return false
    }

    return auditResults
      .map((row) => ({
        ...row,
        pii_matches: deduplicateMatches(row.pii_matches),
      }))
      .filter((row) => !shouldIgnoreLocation(row.page_location))
  }, [auditResults, normalizedIgnoreData])

  const handleResolve = async (pageLocation) => {
    if (!propInfo?.id || !pageLocation) return
    const trimmed = pageLocation.trim()
    if (!trimmed) return
    if (normalizedIgnoreData.resolved.has(trimmed)) return
    setResolvingUrl(trimmed)
    setRulesError(null)
    try {
      const { data, error } = await supabase
        .from("ga4_page_location_ignore_rules")
        .insert({
          ga4_property_id: propInfo.id,
          rule_type: "page_location",
          value: trimmed,
        })
        .select()
        .single()
      if (error) throw error
      setIgnoreRules((prev) => [...prev, data].filter(Boolean))
    } catch (err) {
      console.error("Failed to resolve page location", err)
      if (err?.code === "23505") {
        setRulesError({ message: "This URL is already marked as resolved." })
      } else {
        setRulesError(err)
      }
    } finally {
      setResolvingUrl(null)
    }
  }

  const handleAddUtmRule = async () => {
    if (!propInfo?.id) return
    const parameter = newUtmParameter.trim().toLowerCase()
    const value = newUtmValue.trim().toLowerCase()
    if (!parameter || !value) {
      setRulesError({ message: "UTM parameter and value are required." })
      return
    }
    setAddingUtmRule(true)
    setRulesError(null)
    try {
      const { data, error } = await supabase
        .from("ga4_page_location_ignore_rules")
        .insert({
          ga4_property_id: propInfo.id,
          rule_type: "utm_parameter",
          parameter,
          value,
        })
        .select()
        .single()
      if (error) throw error
      setIgnoreRules((prev) => [...prev, data].filter(Boolean))
      setNewUtmParameter("")
      setNewUtmValue("")
    } catch (err) {
      console.error("Failed to add UTM ignore rule", err)
      if (err?.code === "23505") {
        setRulesError({ message: "This UTM rule already exists." })
      } else {
        setRulesError(err)
      }
    } finally {
      setAddingUtmRule(false)
    }
  }

  const handleRemoveRule = async (ruleId) => {
    if (!ruleId) return
    setRemovingRuleId(ruleId)
    setRulesError(null)
    try {
      const { error } = await supabase
        .from("ga4_page_location_ignore_rules")
        .delete()
        .eq("id", ruleId)
      if (error) throw error
      setIgnoreRules((prev) => prev.filter((rule) => rule.id !== ruleId))
    } catch (err) {
      console.error("Failed to remove ignore rule", err)
      setRulesError(err)
    } finally {
      setRemovingRuleId(null)
    }
  }

  return (
    <div className="page-container">
      <h2>PII In URL</h2>
      <button onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId || "")}`)}>
        Back
      </button>
      {loading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : auditError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load page location audit data.
        </p>
      ) : (
        <>
          {propInfo ? (
            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ margin: 0 }}>
                {propInfo.property_name}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                    marginLeft: "0.5rem",
                  }}
                >
                  ({propInfo.property_id})
                </span>
              </h3>
            </div>
          ) : (
            <p style={{ marginTop: "1rem" }}>Property not found.</p>
          )}
          {rulesError ? (
            <p style={{ marginTop: "1rem", color: "#b00020" }}>
              {typeof rulesError?.message === "string" && rulesError.message
                ? rulesError.message
                : "Ignore rule update failed."}
            </p>
          ) : null}
          <div style={{ marginTop: "1.5rem" }}>
            <h3 style={{ margin: "0 0 0.75rem 0" }}>Potential PII in Page Locations</h3>
            <div
              style={{
                marginBottom: "1rem",
                padding: "1rem",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                backgroundColor: "#fafafa",
              }}
            >
              <h4 style={{ margin: "0 0 0.75rem 0" }}>Ignore Rules</h4>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                  alignItems: "flex-end",
                }}
              >
                <label style={{ display: "flex", flexDirection: "column", minWidth: "160px" }}>
                  <span style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                    UTM parameter
                  </span>
                  <input
                    type="text"
                    value={newUtmParameter}
                    onChange={(event) => setNewUtmParameter(event.target.value)}
                    placeholder="utm_source"
                    style={{ padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                </label>
                <label style={{ display: "flex", flexDirection: "column", minWidth: "180px" }}>
                  <span style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                    Value contains
                  </span>
                  <input
                    type="text"
                    value={newUtmValue}
                    onChange={(event) => setNewUtmValue(event.target.value)}
                    placeholder="internal"
                    style={{ padding: "0.35rem", borderRadius: "4px", border: "1px solid #ccc" }}
                  />
                </label>
                <button type="button" onClick={handleAddUtmRule} disabled={addingUtmRule}>
                  {addingUtmRule ? "Adding…" : "Add UTM Rule"}
                </button>
              </div>
              {normalizedIgnoreData.utmRules.length ? (
                <div style={{ marginTop: "0.75rem" }}>
                  <strong style={{ display: "block", marginBottom: "0.35rem" }}>
                    Active UTM ignore rules
                  </strong>
                  <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                    {ignoreRules
                      .filter((rule) => rule.rule_type === "utm_parameter")
                      .map((rule) => (
                        <li key={rule.id} style={{ marginBottom: "0.35rem" }}>
                          <span>
                            {rule.parameter} contains "{rule.value}"
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveRule(rule.id)}
                            disabled={removingRuleId === rule.id}
                            style={{ marginLeft: "0.75rem" }}
                          >
                            {removingRuleId === rule.id ? "Removing…" : "Remove"}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              ) : null}
              {ignoreRules.some((rule) => rule.rule_type === "page_location") ? (
                <div style={{ marginTop: "0.75rem" }}>
                  <strong style={{ display: "block", marginBottom: "0.35rem" }}>
                    Resolved URLs
                  </strong>
                  <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                    {ignoreRules
                      .filter((rule) => rule.rule_type === "page_location")
                      .map((rule) => (
                        <li key={rule.id} style={{ marginBottom: "0.35rem" }}>
                          <span>{rule.value}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveRule(rule.id)}
                            disabled={removingRuleId === rule.id}
                            style={{ marginLeft: "0.75rem" }}
                          >
                            {removingRuleId === rule.id ? "Removing…" : "Reopen"}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              ) : null}
            </div>
            {groupedResults.length ? (
              <table className="ui-table"
                border="1"
                cellPadding="8"
                style={{ width: "100%", marginTop: "1rem", tableLayout: "fixed" }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "45%" }}>Page Location</th>
                    <th>PII Detected</th>
                    <th style={{ width: "12%" }}>Total Events</th>
                    <th style={{ width: "12%" }}>First Detected</th>
                    <th style={{ width: "12%" }}>Last Detected</th>
                    <th style={{ width: "12%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedResults.map((row) => (
                    <tr key={row.page_location}>
                      <td style={{ wordBreak: "break-all" }}>
                        {row.page_location ? (
                          <a href={row.page_location} target="_blank" rel="noreferrer">
                            {row.page_location}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {row.pii_matches.length ? (
                          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                            {row.pii_matches.map((match, index) => (
                              <li key={`${row.page_location}-${match.type}-${index}`}>
                                <strong style={{ textTransform: "capitalize" }}>
                                  {match.type}
                                </strong>
                                {match.parameter && (
                                  <span style={{ color: "#555", marginLeft: "0.35rem" }}>
                                    ({match.parameter})
                                  </span>
                                )}
                                {match.value && (
                                  <div style={{ fontSize: "0.85rem", color: "#333" }}>
                                    {match.value}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {row.event_count === null ? "—" : formatNumber(row.event_count)}
                      </td>
                      <td>{formatDate(row.first_detected)}</td>
                      <td>{formatDate(row.last_detected)}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleResolve(row.page_location)}
                          disabled={
                            resolvingUrl === row.page_location ||
                            resolvingUrl === row.page_location?.trim()
                          }
                        >
                          {resolvingUrl === row.page_location ||
                          resolvingUrl === row.page_location?.trim()
                            ? "Resolving…"
                            : "Resolved"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ marginTop: "1rem" }}>
                No PII detected in page locations for the selected property.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function normalizeMatch(match) {
  if (!match || typeof match !== "object") return null
  const type = typeof match.type === "string" ? match.type.toLowerCase() : ""
  if (!type) return null
  const parameter =
    typeof match.parameter === "string" && match.parameter
      ? match.parameter
      : null
  let value = typeof match.value === "string" ? match.value.trim() : ""
  if (value.length > 250) value = `${value.slice(0, 247)}…`
  return {
    type,
    parameter,
    value,
  }
}

function deduplicateMatches(matches) {
  const seen = new Set()
  const result = []
  for (const match of matches) {
    const key = `${match.type}|${match.parameter || ""}|${match.value || ""}`
    if (seen.has(key)) continue
    seen.add(key)
    result.push(match)
  }
  return result
}

